import StatusLabel from 'components/status-label/StatusLabel';
import {
  EntertainmentVideoStatus,
  FullEntertainmentVideo
} from 'modules/entertainment-videos/types';
import { ENTERTAINMENT_VIDEO_STATUS_TO_STATUS_LABEL_TYPE } from 'modules/entertainment-videos/constants';
import moment from 'moment';
import React, { ReactNode, useState } from 'react';
import { __t, applyTranslations } from 'utils/language';
import './entertainment-video-review-info.scss';
import ActionButton from 'components/buttons/action-button/ActionButton';
import ConfirmModal from 'components/modal/confirm-modal/ConfirmModal';

applyTranslations(require('./entertainment-video-review-info.i18n.json'));

function translate(part: string) {
  return __t(`entertainmentVideoReviewInfo${part}`);
}

function formatDate(date: string) {
  return moment(date).format('DD/MM/YYYY');
}

export function EntertainmentVideoReviewInfo({
  fullEntertainmentVideo,
  isEditMode,
  onEditClick,
  onUnpublishClick
}: {
  fullEntertainmentVideo: FullEntertainmentVideo,
  isEditMode: boolean,
  onEditClick: () => void,
  onUnpublishClick: () => void
}) {
  return (
    <React.Fragment>
      {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
      <InfoList fullEntertainmentVideo={fullEntertainmentVideo} />
      {!isEditMode && (
        <div className="ev-review-info__btn-container">
          {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
          <Action
            currentStatus={fullEntertainmentVideo.entertainmentVideo.status}
            onEditClick={onEditClick}
            onUnpublishClick={onUnpublishClick}
          />
        </div>
      )}
    </React.Fragment>
  );
}

function InfoList({
  fullEntertainmentVideo
}: {
  fullEntertainmentVideo: FullEntertainmentVideo
}) {
  const { status } = fullEntertainmentVideo.entertainmentVideo;

  return (
    <ul className="ev-review-info__info-list">
      {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
      <InfoListItem
        label={translate('.labels.submissionDate')}
        value={formatDate(fullEntertainmentVideo.entertainmentVideo.createdAt)}
      />
      {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
      <InfoListItem
        label={translate('.labels.reviewDate')}
        value={
          typeof fullEntertainmentVideo.entertainmentVideo.reviewedAt ===
          'string'
            ? formatDate(fullEntertainmentVideo.entertainmentVideo.reviewedAt)
            : undefined
        }
      />
      {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
      <InfoListItem
        label={translate('.labels.status')}
        value={
          <StatusLabel
            size="small"
            type={ENTERTAINMENT_VIDEO_STATUS_TO_STATUS_LABEL_TYPE[status]}
            label={translate(`.enum.status.${status}`)}
          />
        }
      />
      {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
      <InfoListItem
        label={translate('.labels.items')}
        value={fullEntertainmentVideo.items.length}
      />
      {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
      <InfoListItem
        label={translate('.labels.id')}
        value={fullEntertainmentVideo.entertainmentVideo.id}
      />
    </ul>
  );
}

function InfoListItem({ label, value }: { label: string, value: ReactNode }) {
  return (
    <li>
      <div className="outfit-review-info__label">{label}</div>
      <div className="outfit-review-info__value">{value || '–'}</div>
    </li>
  );
}

function Action({
  currentStatus,
  onEditClick,
  onUnpublishClick
}: {
  currentStatus: EntertainmentVideoStatus,
  onEditClick: () => void,
  onUnpublishClick: () => void
}) {
  const [isConfirmUnpublishModalOpen, setIsConfirmUnpublishModalOpen] = useState(false);

  const isEditable =
    currentStatus === EntertainmentVideoStatus.PENDING ||
    currentStatus === EntertainmentVideoStatus.PUBLISH_FAILED ||
    currentStatus === EntertainmentVideoStatus.UNPUBLISHED;
  const isUnpublishable =
    currentStatus === EntertainmentVideoStatus.PUBLISHED ||
    currentStatus === EntertainmentVideoStatus.UNPUBLISH_FAILED;

  function closeConfirmUnpublishModal(): void {
    setIsConfirmUnpublishModalOpen(false);
  }

  if (isEditable) {
    return (
      <ActionButton onClick={onEditClick}>
        {translate('.labels.edit')}
      </ActionButton>
    );
  }

  if (isUnpublishable) {
    return (
      <React.Fragment>
        <ActionButton color="primary" onClick={() => setIsConfirmUnpublishModalOpen(true)}>
          {translate('.labels.unpublish')}
        </ActionButton>
        <ConfirmModal
          open={isConfirmUnpublishModalOpen}
          title={translate('.unpublishModal.title')}
          confirmButtonColor="primary"
          confirmButtonLabel={translate('.unpublishModal.buttonLabel')}
          onConfirm={() => {
            onUnpublishClick();
            closeConfirmUnpublishModal();
          }}
          onCancel={() => {
            closeConfirmUnpublishModal();
          }}
          onClose={() => {
            closeConfirmUnpublishModal();
          }}
        />
      </React.Fragment>
    );
  }

  return null;
}
